import PropTypes from "prop-types";
import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import withRouter from "components/Common/withRouter";
import {
  changeLayout,
  changeLayoutMode,
  changeSidebarTheme,
  changeSidebarThemeImage,
  changeSidebarType,
  changeTopbarTheme,
  changeLayoutWidth,
  showRightSidebarAction,
  changeFontScale,
  changeFontFamily,
  changeFontColor,
  changeUiTheme,
} from "../../store/actions";

// Layout Related Components
import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import RightSidebar from "../CommonForBoth/RightSidebar";

//redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from 'reselect';
import { useIdleMouseSessionTimeout } from "helpers/useIdleMouseSessionTimeout";
import AncileHiringWidget from "components/Common/AncileHiringWidget";

const MENU_LAYOUT_STORAGE_KEY = "app-menu-layout";

const Layout = props => {
  const dispatch = useDispatch();
  useIdleMouseSessionTimeout();
  const [menuLayoutMode, setMenuLayoutMode] = useState(() => {
    try {
      const savedValue = localStorage.getItem(MENU_LAYOUT_STORAGE_KEY);
      return savedValue === "horizontal" ? "horizontal" : "vertical";
    } catch (_error) {
      return "vertical";
    }
  });

  const selectLayoutProperties = createSelector(
    (state) => state.Layout,
    (layout) => ({
      isPreloader: layout.isPreloader,
      layoutModeType: layout.layoutModeType,
      leftSideBarThemeImage: layout.leftSideBarThemeImage,
      leftSideBarType: layout.leftSideBarType,
      layoutWidth: layout.layoutWidth,
      topbarTheme: layout.topbarTheme,
      showRightSidebar: layout.showRightSidebar,
      leftSideBarTheme: layout.leftSideBarTheme,
      fontScale: layout.fontScale,
      fontFamily: layout.fontFamily,
      fontColor: layout.fontColor,
      uiTheme: layout.uiTheme,
    }));

  const {
    isPreloader,
    leftSideBarThemeImage,
    layoutWidth,
    leftSideBarType,
    topbarTheme,
    showRightSidebar,
    leftSideBarTheme,
    layoutModeType,
    fontScale,
    fontFamily,
    fontColor,
    uiTheme,
  } = useSelector(selectLayoutProperties);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  /* Run before setState + child mount so body classes match Redux before Sidebar reads them. */
  const commitMenuLayoutMode = useCallback(
    (next) => {
      if (
        next === "vertical" &&
        menuLayoutMode === "horizontal" &&
        leftSideBarType
      ) {
        document.body.classList.remove("vertical-collpsed", "sidebar-enable");
        document.body.removeAttribute("data-keep-enlarged");
        dispatch(changeSidebarType(leftSideBarType, isMobile));
      }
      setMenuLayoutMode(next);
    },
    [dispatch, isMobile, leftSideBarType, menuLayoutMode]
  );

  const toggleMenuCallback = () => {
    if (leftSideBarType === "default") {
      dispatch(changeSidebarType("condensed", isMobile));
    } else if (leftSideBarType === "condensed") {
      dispatch(changeSidebarType("default", isMobile));
    }
  };

  //hides right sidebar on body click
  const hideRightbar = (event) => {
    var rightbar = document.getElementById("right-bar");
    //if clicked in inside right bar, then do nothing
    if (rightbar && rightbar.contains(event.target)) {
      return;
    } else {
      //if clicked in outside of rightbar then fire action for hide rightbar
      dispatch(showRightSidebarAction(false));
    }
  };

  /*
  layout  settings
  */

  useEffect(() => {
    //init body click event fot toggle rightbar
    document.body.addEventListener("click", hideRightbar, true);


  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(changeLayout("vertical"));
  }, [dispatch]);

  useLayoutEffect(() => {
    document.body.setAttribute("data-app-menu-layout", menuLayoutMode);
    try {
      localStorage.setItem(MENU_LAYOUT_STORAGE_KEY, menuLayoutMode);
    } catch (_error) {}
    if (menuLayoutMode === "horizontal") {
      document.body.classList.remove("sidebar-hidden-full");
    }
  }, [menuLayoutMode]);

  useEffect(() => {
    if (leftSideBarTheme) {
      dispatch(changeSidebarTheme(leftSideBarTheme));
    }
  }, [leftSideBarTheme, dispatch]);

  useEffect(() => {
    if (layoutModeType) {
      dispatch(changeLayoutMode(layoutModeType));
    }
  }, [layoutModeType, dispatch]);

  useEffect(() => {
    if (leftSideBarThemeImage) {
      dispatch(changeSidebarThemeImage(leftSideBarThemeImage));
    }
  }, [leftSideBarThemeImage, dispatch]);

  useEffect(() => {
    if (layoutWidth) {
      dispatch(changeLayoutWidth(layoutWidth));
    }
  }, [layoutWidth, dispatch]);

  useEffect(() => {
    if (leftSideBarType) {
      dispatch(changeSidebarType(leftSideBarType, isMobile));
    }
  }, [leftSideBarType, dispatch, isMobile]);

  useEffect(() => {
    if (topbarTheme) {
      dispatch(changeTopbarTheme(topbarTheme));
    }
  }, [topbarTheme, dispatch]);

  useEffect(() => {
    if (fontScale) {
      dispatch(changeFontScale(fontScale));
    }
  }, [fontScale, dispatch]);

  useEffect(() => {
    if (fontFamily) {
      dispatch(changeFontFamily(fontFamily));
    }
  }, [fontFamily, dispatch]);

  useEffect(() => {
    if (fontColor) {
      dispatch(changeFontColor(fontColor));
    }
  }, [fontColor, dispatch]);

  useEffect(() => {
    if (uiTheme) {
      dispatch(changeUiTheme(uiTheme));
    }
  }, [uiTheme, dispatch]);

  return (
    <React.Fragment>

      <div id="layout-wrapper">
        <Header
          toggleMenuCallback={toggleMenuCallback}
          menuLayoutMode={menuLayoutMode}
          onMenuLayoutChange={commitMenuLayoutMode}
        />
        {menuLayoutMode === "vertical" ? (
          <Sidebar
            theme={leftSideBarTheme}
            type={leftSideBarType}
            isMobile={isMobile}
          />
        ) : null}
        <div className="main-content">{props.children}</div>
        <Footer />
      </div>
      {showRightSidebar ? <RightSidebar /> : null}
      <AncileHiringWidget />
    </React.Fragment>
  );
};

Layout.propTypes = {
  changeLayoutWidth: PropTypes.func,
  changeSidebarTheme: PropTypes.func,
  changeSidebarThemeImage: PropTypes.func,
  changeSidebarType: PropTypes.func,
  changeTopbarTheme: PropTypes.func,
  children: PropTypes.any,
  isPreloader: PropTypes.any,
  layoutWidth: PropTypes.any,
  leftSideBarTheme: PropTypes.any,
  leftSideBarThemeImage: PropTypes.any,
  leftSideBarType: PropTypes.any,
  location: PropTypes.object,
  showRightSidebar: PropTypes.any,
  topbarTheme: PropTypes.any,
};

export default withRouter(Layout);
